import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Alert, Badge, Button, Form, Spinner } from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBan,
  faBookmark,
  faCheck,
  faChevronDown,
  faChevronRight,
  faDiagramProject,
  faEyeSlash,
  faFilter,
  faLayerGroup,
  faList,
  faPause,
  faPlay,
  faPlus,
  faTableColumns,
  faTrash,
  faUserGroup,
  faWandMagicSparkles,
} from "@fortawesome/free-solid-svg-icons";

import api from "../api/API";
import { useAuth } from "../hooks/useAuth";
import { useWorkspace } from "../context/WorkspaceContext";
import { useTimer } from "../context/TimerContext";
import TaskKanbanView from "../components/TaskKanbanView";
import TaskModal from "../components/Modals/TaskModal";
import SalvarViewModal from "../components/SalvarViewModal";
import AplicarModeloModal from "../components/AplicarModeloModal";
import {
  atualizarView,
  criarView,
  listarViews,
  removerView,
} from "../api/taskViewsApi";
import {
  GROUP_BY_OPTIONS,
  agruparTarefas,
  estaAtrasada,
  getDueBucket,
  getResponsavelNome,
} from "../utils/taskGrouping";
import {
  FILTROS_VAZIOS,
  RESPONSAVEL_EU,
  RESPONSAVEL_SEM_DONO,
  VIEWS_PADRAO,
  VIEW_MODE,
  VIEW_PADRAO_ID,
  adaptaAoLeitor,
  alternarViewOculta,
  carregarViewsOcultas,
  coletarViewsLegadas,
  descreverView,
  ehCompartilhada,
  normalizarViewDoServidor,
  paraPayload,
  viewFoiAlterada,
} from "../utils/taskViews";
import {
  TASK_PRIORITY_OPTIONS,
  getTaskPriorityLabel,
  getTaskPriorityClass,
} from "../utils/taskPriority";
import { formatarCronometro, formatarDuracao, segundosDaTarefa } from "../utils/taskTime";
import "./TarefasCentralPage.css";

const parseCollection = (resposta) => {
  if (Array.isArray(resposta?.data)) return resposta.data;
  if (resposta?.data?.status === false) {
    throw new Error(resposta?.data?.message || "Falha ao carregar dados.");
  }
  return resposta?.data?.records || resposta?.data?.data || [];
};

const normalizar = (valor = "") => String(valor).trim().toLowerCase();

const OPCOES_PRAZO = [
  { value: "all", label: "Qualquer prazo" },
  { value: "atrasada", label: "Atrasadas" },
  { value: "hoje", label: "Hoje" },
  { value: "semana", label: "Esta semana" },
  { value: "futuro", label: "Depois" },
  { value: "sem-prazo", label: "Sem prazo" },
];

const OPCOES_STATUS = [
  { value: "all", label: "Qualquer status" },
  { value: "pendente", label: "Pendentes" },
  { value: "concluida", label: "Concluidas" },
];

const formatarData = (valor) => {
  if (!valor) return null;
  const data = new Date(valor);
  return Number.isNaN(data.getTime()) ? null : data.toLocaleDateString("pt-BR");
};

const VIEW_INICIAL = VIEWS_PADRAO.find((item) => item.id === VIEW_PADRAO_ID) || VIEWS_PADRAO[0];

const TarefasCentralPage = () => {
  const { user } = useAuth();
  const { selectedWorkspaceId, loading: carregandoWorkspaces } = useWorkspace();
  const {
    alternar: alternarCronometro,
    emAndamento,
    segundos: segundosCorrentes,
    ocupado: cronometroOcupado,
    versao: versaoCronometro,
  } = useTimer();

  const usuarioId = user?.id ?? user?.userId ?? null;

  const [tarefas, setTarefas] = useState([]);
  const [etapas, setEtapas] = useState([]);
  const [membros, setMembros] = useState([]);
  const [tags, setTags] = useState([]);

  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState("");

  // Estado da view em uso (pode divergir da view salva ate você salvar).
  // Ja nasce na view padrao: entrar sem recorte nenhum e o que tornava a tela
  // antiga confusa.
  const [viewsCustom, setViewsCustom] = useState([]);
  const [viewAtivaId, setViewAtivaId] = useState(VIEW_PADRAO_ID);
  const [filtros, setFiltros] = useState(VIEW_INICIAL.filtros);
  const [agruparPor, setAgruparPor] = useState(VIEW_INICIAL.agruparPor);
  const [modo, setModo] = useState(VIEW_INICIAL.modo);
  const [ocultarConcluidas, setOcultarConcluidas] = useState(
    VIEW_INICIAL.ocultarConcluidas !== false
  );
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const [viewsOcultas, setViewsOcultas] = useState([]);
  const [mostrarSalvarView, setMostrarSalvarView] = useState(false);
  const [salvandoView, setSalvandoView] = useState(false);
  const [erroView, setErroView] = useState("");

  const [selecionadas, setSelecionadas] = useState([]);
  const [aplicandoLote, setAplicandoLote] = useState(false);
  const [expandidos, setExpandidos] = useState([]);

  const [tarefaAberta, setTarefaAberta] = useState(null);
  const [mostrarModal, setMostrarModal] = useState(false);
  const [mostrarAplicarModelo, setMostrarAplicarModelo] = useState(false);

  const todasAsViews = useMemo(() => [...VIEWS_PADRAO, ...viewsCustom], [viewsCustom]);

  // Views do time que a pessoa escolheu não ver continuam existindo; só somem
  // da barra dela. É o que impede a aba virar um lixão quando o time cresce.
  const views = useMemo(
    () =>
      todasAsViews.filter(
        (item) => item.id === viewAtivaId || !viewsOcultas.includes(String(item.id))
      ),
    [todasAsViews, viewsOcultas, viewAtivaId]
  );

  const viewAtiva = useMemo(
    () => todasAsViews.find((item) => String(item.id) === String(viewAtivaId)) || VIEWS_PADRAO[0],
    [todasAsViews, viewAtivaId]
  );

  const alterada = useMemo(
    () => viewFoiAlterada(viewAtiva, { filtros, agruparPor, modo, ocultarConcluidas }),
    [viewAtiva, filtros, agruparPor, modo, ocultarConcluidas]
  );

  // ─── Carregamento ───────────────────────────────────────────────────────────

  const carregar = useCallback(async ({ silencioso = false } = {}) => {
    if (!selectedWorkspaceId) {
      setTarefas([]);
      setCarregando(false);
      return;
    }

    // O polling do quadro chama isso a cada poucos segundos - se acionasse o
    // spinner de tela cheia toda vez, a pagina inteira "piscaria"
    // regularmente. So o carregamento de verdade (troca de workspace, entrada
    // na pagina) mostra o spinner; atualizacao em segundo plano so troca os
    // dados quando a resposta chega.
    if (!silencioso) {
      setCarregando(true);
    }
    setErro("");

    try {
      // Etapas, membros e tags sao acessorios: se falharem, a lista ainda serve.
      const [tarefasRes, etapasRes, membrosRes, tagsRes] = await Promise.all([
        api.get(`/tasks?workspaceId=${selectedWorkspaceId}`),
        api.get(`/workspaces/${selectedWorkspaceId}/kanban/etapas`).catch(() => null),
        api.get(`/workspaces/${selectedWorkspaceId}/members`).catch(() => null),
        api.get(`/workspaces/${selectedWorkspaceId}/task-tags`).catch(() => null),
      ]);

      setTarefas(parseCollection(tarefasRes));
      setEtapas(etapasRes ? parseCollection(etapasRes) : []);
      setMembros(membrosRes ? parseCollection(membrosRes) : []);
      setTags(tagsRes ? parseCollection(tagsRes) : []);
    } catch (error) {
      console.error("TarefasCentral: erro ao carregar", error);
      // Atualizacao silenciosa que falha nao pode apagar o que ja estava na
      // tela (ex.: instabilidade momentanea de rede) - so o carregamento
      // "de verdade" limpa a lista e mostra o erro.
      if (!silencioso) {
        setErro(error?.response?.data?.message || error?.message || "Erro ao carregar tarefas.");
        setTarefas([]);
      }
    } finally {
      if (!silencioso) {
        setCarregando(false);
      }
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  // Iniciar ou parar o cronometro (aqui, no modal ou no widget do topo) muda o
  // acumulado das tarefas; sem recarregar, a lista mostraria o total de antes.
  const versaoCronometroLida = useRef(versaoCronometro);

  useEffect(() => {
    if (versaoCronometroLida.current === versaoCronometro) return;
    versaoCronometroLida.current = versaoCronometro;
    carregar();
  }, [versaoCronometro, carregar]);

  // No quadro, um agente do pipeline pode mover um card sem ninguem tocar na
  // tela - sem isso, so via ao mudar de coluna quem reabrisse a pagina.
  // So liga no modo QUADRO: na lista, recarregar sozinho no meio de uma
  // edicao atrapalha mais do que ajuda.
  //
  // Limitacao conhecida: se o polling cair bem no meio de um drag-and-drop
  // (arrastar um card), o estado otimista local pode ser sobrescrito antes do
  // PATCH confirmar - o proximo poll corrige sozinho, mas por ~8s a tela
  // poderia "voltar" o card. Baixo impacto, nao tratado aqui.
  useEffect(() => {
    if (modo !== VIEW_MODE.QUADRO || !selectedWorkspaceId) return undefined;

    const intervalo = setInterval(() => {
      carregar({ silencioso: true });
    }, 8000);

    return () => clearInterval(intervalo);
  }, [modo, selectedWorkspaceId, carregar]);

  const carregarViews = useCallback(async () => {
    if (!selectedWorkspaceId) {
      setViewsCustom([]);
      return;
    }

    try {
      // Views criadas antes de existir persistencia no banco sobem uma vez e a
      // chave antiga e apagada. Sem isso, quem ja tinha view perderia tudo.
      const legadas = coletarViewsLegadas(selectedWorkspaceId);

      if (legadas.length) {
        await Promise.all(
          legadas.map((legada) =>
            criarView(selectedWorkspaceId, paraPayload(legada)).catch(() => null)
          )
        );
      }

      const registros = await listarViews(selectedWorkspaceId);
      setViewsCustom(registros.map(normalizarViewDoServidor));
    } catch (error) {
      console.error("TarefasCentral: erro ao carregar views", error);
      setViewsCustom([]);
    }
  }, [selectedWorkspaceId]);

  useEffect(() => {
    carregarViews();
    setViewsOcultas(carregarViewsOcultas(selectedWorkspaceId));
  }, [carregarViews, selectedWorkspaceId]);

  const aplicarView = useCallback((view) => {
    setViewAtivaId(view.id);
    setFiltros({ ...FILTROS_VAZIOS, ...view.filtros });
    setAgruparPor(view.agruparPor);
    setModo(view.modo);
    setOcultarConcluidas(view.ocultarConcluidas !== false);
    setSelecionadas([]);
  }, []);

  // ─── Opcoes derivadas ───────────────────────────────────────────────────────

  const projetoOptions = useMemo(() => {
    const mapa = new Map();
    tarefas.forEach((task) => {
      if (task.id_projeto && task.projeto) mapa.set(String(task.id_projeto), task.projeto);
    });
    return Array.from(mapa.entries()).map(([value, label]) => ({ value, label }));
  }, [tarefas]);

  const responsavelOptions = useMemo(() => {
    const mapa = new Map();

    // Membros do workspace primeiro; tarefas cobrem quem saiu mas ainda tem carga.
    membros.forEach((membro) => {
      const id = String(membro?.id_usuario ?? membro?.user_id ?? membro?.id ?? "");
      if (id) mapa.set(id, membro.descricao || membro.nome || membro.email || `Usuario ${id}`);
    });

    tarefas.forEach((task) => {
      const id = String(task.id_usuario_responsavel || "");
      if (id && !mapa.has(id)) mapa.set(id, getResponsavelNome(task) || `Usuario ${id}`);
    });

    return Array.from(mapa.entries()).map(([value, label]) => ({ value, label }));
  }, [membros, tarefas]);

  // Nomes para a descricao legivel da view traduzir ids em palavras.
  const contextoDescricao = useMemo(
    () => ({
      projetos: projetoOptions,
      responsaveis: responsavelOptions,
      tags: tags.map((tag) => ({ value: String(tag.id), label: tag.nome })),
    }),
    [projetoOptions, responsavelOptions, tags]
  );

  // ─── Filtro ─────────────────────────────────────────────────────────────────

  const tarefasFiltradas = useMemo(() => {
    return tarefas.filter((task) => {
      if (ocultarConcluidas && task.concluido) return false;

      if (filtros.busca) {
        const blob = [
          task.descricao,
          task.detalhe,
          task.projeto,
          getResponsavelNome(task),
          ...(task.tags || []).map((tag) => tag.nome),
        ]
          .filter(Boolean)
          .join(" ");
        if (!normalizar(blob).includes(normalizar(filtros.busca))) return false;
      }

      if (filtros.projeto !== "all" && String(task.id_projeto) !== filtros.projeto) return false;

      if (filtros.responsavel !== "all") {
        // A sentinela "me" so vira id aqui, onde o usuario logado e conhecido.
        const alvo = filtros.responsavel === RESPONSAVEL_EU ? String(usuarioId || "") : filtros.responsavel;

        if (filtros.responsavel === RESPONSAVEL_SEM_DONO) {
          if (task.id_usuario_responsavel) return false;
        } else if (String(task.id_usuario_responsavel || "") !== alvo) {
          return false;
        }
      }

      if (filtros.prazo !== "all" && getDueBucket(task.vencimento) !== filtros.prazo) return false;

      if (filtros.prioridade !== "all" && String(task.prioridade) !== filtros.prioridade) return false;

      if (filtros.status !== "all") {
        if (filtros.status === "concluida" && !task.concluido) return false;
        if (filtros.status === "pendente" && task.concluido) return false;
      }

      if (filtros.tag !== "all" && !(task.tags || []).some((tag) => String(tag.id) === filtros.tag)) {
        return false;
      }

      return true;
    });
  }, [tarefas, filtros, ocultarConcluidas, usuarioId]);

  // Checklist completa de cada pai, vinda da base sem filtro: expandir uma
  // implantacao tem de mostrar todos os passos, inclusive os que o filtro atual
  // esconderia (de outro responsavel, ja concluidos).
  const subtarefasPorPai = useMemo(() => {
    const mapa = new Map();

    tarefas
      .filter((task) => task.id_tarefa)
      .forEach((sub) => {
        const chave = Number(sub.id_tarefa);
        if (!mapa.has(chave)) mapa.set(chave, []);
        mapa.get(chave).push(sub);
      });

    mapa.forEach((lista) => lista.sort((a, b) => (a.ordem || 0) - (b.ordem || 0)));
    return mapa;
  }, [tarefas]);

  /**
   * Subtarefa nunca e linha de primeiro nivel quando o pai esta na lista.
   * Quando o pai NAO esta (o passo e seu, a implantacao e de outra pessoa), ela
   * aparece solta com a trilha do pai - senao o trabalho sumiria da sua view.
   */
  const tarefasDaLista = useMemo(() => {
    const principais = tarefasFiltradas.filter((task) => !task.id_tarefa);
    const idsPrincipais = new Set(principais.map((task) => Number(task.id)));

    const orfas = tarefasFiltradas.filter(
      (task) => task.id_tarefa && !idsPrincipais.has(Number(task.id_tarefa))
    );

    return [...principais, ...orfas];
  }, [tarefasFiltradas]);

  const grupos = useMemo(
    () => agruparTarefas(tarefasDaLista, agruparPor, { etapas }),
    [tarefasDaLista, agruparPor, etapas]
  );

  const metricas = useMemo(
    () => ({
      total: tarefasDaLista.length,
      atrasadas: tarefasDaLista.filter(estaAtrasada).length,
      hoje: tarefasDaLista.filter(
        (task) => !task.concluido && getDueBucket(task.vencimento) === "hoje"
      ).length,
      // Esforco ja gasto no recorte atual: e o numero que responde "quanto essa
      // frente custou" sem sair da view.
      segundos: tarefasDaLista.reduce(
        (soma, task) => soma + Number(task.tempo_segundos_total || 0),
        0
      ),
    }),
    [tarefasDaLista]
  );

  // ─── Selecao ────────────────────────────────────────────────────────────────

  const alternarSelecao = (id) => {
    setSelecionadas((atual) =>
      atual.includes(id) ? atual.filter((item) => item !== id) : [...atual, id]
    );
  };

  const alternarGrupo = (grupo) => {
    const ids = grupo.tarefas.map((task) => task.id);
    const todosMarcados = ids.every((id) => selecionadas.includes(id));

    setSelecionadas((atual) =>
      todosMarcados
        ? atual.filter((id) => !ids.includes(id))
        : Array.from(new Set([...atual, ...ids]))
    );
  };

  /** Aplica o mesmo patch em todas as selecionadas e recarrega uma vez só. */
  async function aplicarEmLote(fields) {
    if (!selecionadas.length) return;

    setAplicandoLote(true);
    setErro("");

    try {
      await Promise.all(
        selecionadas.map((id) => api.put(`/tasks/${id}`, { fields }))
      );
      setSelecionadas([]);
      await carregar();
    } catch (error) {
      console.error("TarefasCentral: erro na acao em lote", error);
      setErro(error?.response?.data?.message || "Nao foi possivel aplicar a alteracao em lote.");
    } finally {
      setAplicandoLote(false);
    }
  }

  // ─── Acoes ──────────────────────────────────────────────────────────────────

  async function concluir(task) {
    const antes = tarefas;
    setTarefas((atual) =>
      atual.map((item) => (item.id === task.id ? { ...item, concluido: !item.concluido } : item))
    );

    try {
      await api.put(`/tasks/${task.id}`, { fields: { concluido: !task.concluido } });

      // Concluir um passo pode fechar a implantacao inteira (auto-conclusao do
      // pai acontece no servidor), entao recarregamos para refletir isso.
      if (task.id_tarefa || Number(task.total_subtarefas) > 0) {
        await carregar();
      }
    } catch (error) {
      console.error("TarefasCentral: erro ao concluir", error);
      setTarefas(antes);
      setErro(error?.response?.data?.message || "Nao foi possivel atualizar a tarefa.");
    }
  }

  // O TaskKanbanView ja fez o PATCH de etapa no servidor antes de chamar isto -
  // so precisa refletir localmente, sem recarregar tudo (senao o quadro inteiro
  // pisca com o spinner de carregando a cada arraste).
  function handleTaskMoved(taskId, novaEtapaId) {
    setTarefas((atual) =>
      atual.map((item) => (String(item.id) === String(taskId) ? { ...item, id_etapa: novaEtapaId } : item))
    );
  }

  function abrirTarefa(task) {
    setTarefaAberta(task);
    setMostrarModal(true);
  }

  function alternarExpandir(id) {
    setExpandidos((atual) =>
      atual.includes(id) ? atual.filter((item) => item !== id) : [...atual, id]
    );
  }

  /** Dispensa um passo sem mentir que foi feito. */
  async function alternarNaoSeAplica(task) {
    const antes = tarefas;
    const novoValor = !task.nao_se_aplica;

    setTarefas((atual) =>
      atual.map((item) => (item.id === task.id ? { ...item, nao_se_aplica: novoValor } : item))
    );

    try {
      await api.put(`/tasks/${task.id}`, { fields: { nao_se_aplica: novoValor } });
      // O pai pode ter fechado: o servidor decide, a tela so recarrega.
      await carregar();
    } catch (error) {
      console.error("TarefasCentral: erro ao dispensar passo", error);
      setTarefas(antes);
      setErro(error?.response?.data?.message || "Nao foi possivel atualizar o passo.");
    }
  }

  function novaTarefa() {
    setTarefaAberta(null);
    setMostrarModal(true);
  }

  async function salvarView({ nome, compartilhamento, sobrescrever }) {
    setSalvandoView(true);
    setErroView("");

    const payload = {
      nome,
      filtros,
      agruparPor,
      modo,
      ocultarConcluidas,
      compartilhamento,
    };

    try {
      // Sobrescrever so vale para view sua; a partir de uma view do sistema ou
      // de outra pessoa, salvar sempre cria uma copia sua.
      const salva = sobrescrever
        ? await atualizarView(viewAtiva.id, payload)
        : await criarView(selectedWorkspaceId, payload);

      await carregarViews();
      if (salva?.id) setViewAtivaId(salva.id);

      setMostrarSalvarView(false);
    } catch (error) {
      console.error("TarefasCentral: erro ao salvar view", error);
      setErroView(
        error?.response?.data?.message || error?.message || "Nao foi possivel salvar a view."
      );
    } finally {
      setSalvandoView(false);
    }
  }

  async function excluirView(event, view) {
    event.stopPropagation();
    if (!window.confirm(`Excluir a view "${view.nome}"? Isso vale para todo o time.`)) return;

    try {
      await removerView(view.id);
      await carregarViews();
      if (String(viewAtivaId) === String(view.id)) aplicarView(VIEWS_PADRAO[0]);
    } catch (error) {
      console.error("TarefasCentral: erro ao excluir view", error);
      setErro(error?.response?.data?.message || "Nao foi possivel excluir a view.");
    }
  }

  /** Esconde da barra sem apagar: a view continua existindo para o time. */
  function ocultarView(event, view) {
    event.stopPropagation();
    setViewsOcultas(alternarViewOculta(selectedWorkspaceId, view.id));
    if (String(viewAtivaId) === String(view.id)) aplicarView(VIEWS_PADRAO[0]);
  }

  const atualizarFiltro = (campo, valor) => {
    setFiltros((atual) => ({ ...atual, [campo]: valor }));
    setSelecionadas([]);
  };

  const filtrosAtivos = useMemo(() => {
    let total = 0;
    if (filtros.busca) total += 1;
    ["projeto", "responsavel", "prazo", "prioridade", "status", "tag"].forEach((campo) => {
      if (filtros[campo] !== "all") total += 1;
    });
    return total;
  }, [filtros]);

  // ─── Render ─────────────────────────────────────────────────────────────────

  /**
   * Uma funcao, nao um componente: linha de pai e linha de subtarefa sao a
   * mesma coisa com adornos diferentes, e duplicar isso vira divergencia.
   */
  function renderLinha(task, { filhas = [], aberto = false, aninhada = false } = {}) {
    const atrasada = estaAtrasada(task);
    const prazo = formatarData(task.vencimento);
    const total = Number(task.total_subtarefas) || filhas.length || 0;
    const resolvidas = Number(task.subtarefas_concluidas) || 0;
    const dispensada = Boolean(task.nao_se_aplica);
    const ehSubtarefa = Boolean(task.id_tarefa);

    const contandoAqui = Boolean(emAndamento) && Number(emAndamento.id_tarefa) === Number(task.id);
    const segundosTarefa = segundosDaTarefa(task, emAndamento);

    return (
      <article
        key={task.id}
        className={`tc-linha ${task.concluido ? "concluida" : ""} ${
          selecionadas.includes(task.id) ? "selecionada" : ""
        } ${aninhada ? "aninhada" : ""} ${dispensada ? "dispensada" : ""}`}
      >
        <Form.Check
          type="checkbox"
          checked={selecionadas.includes(task.id)}
          onChange={() => alternarSelecao(task.id)}
          aria-label={`Selecionar ${task.descricao}`}
        />

        <button
          type="button"
          className={`tc-check ${task.concluido ? "feita" : ""}`}
          onClick={() => concluir(task)}
          disabled={dispensada}
          title={dispensada ? "Passo dispensado" : task.concluido ? "Reabrir" : "Concluir"}
          aria-label={task.concluido ? "Reabrir tarefa" : "Concluir tarefa"}
        >
          <FontAwesomeIcon icon={faCheck} />
        </button>

        <div className="tc-titulo-area">
          {/* Subtarefa fora do pai carrega a trilha, senao vira orfa sem contexto. */}
          {ehSubtarefa && !aninhada && task.tarefa_pai && (
            <span className="tc-trilha">{task.tarefa_pai} ›</span>
          )}

          <button type="button" className="tc-titulo" onClick={() => abrirTarefa(task)}>
            {task.descricao}
          </button>
        </div>

        {total > 0 && !aninhada && (
          <button
            type="button"
            className={`tc-progresso ${resolvidas >= total ? "completo" : ""}`}
            onClick={() => alternarExpandir(task.id)}
            title={aberto ? "Recolher passos" : "Ver passos"}
          >
            <FontAwesomeIcon icon={aberto ? faChevronDown : faChevronRight} />
            {resolvidas}/{total}
          </button>
        )}

        <div className="tc-meta">
          {/* Um controle so: mostra o esforco acumulado e liga/desliga o
              cronometro. Botao separado do numero dobraria a largura da linha
              para dizer a mesma coisa. */}
          <button
            type="button"
            className={`tc-tempo ${contandoAqui ? "rodando" : ""} ${
              task.tempo_rodando && !contandoAqui ? "alheio" : ""
            } ${segundosTarefa > 0 ? "com-tempo" : ""}`}
            onClick={() => alternarCronometro(task)}
            disabled={cronometroOcupado || dispensada}
            title={
              contandoAqui
                ? "Pausar cronometro"
                : task.tempo_rodando
                ? "Alguem do time esta contando tempo nesta tarefa"
                : segundosTarefa > 0
                ? `Ja foram ${formatarDuracao(segundosTarefa)} — clique para retomar`
                : "Iniciar cronometro"
            }
            aria-label={contandoAqui ? "Pausar cronometro" : "Iniciar cronometro"}
          >
            <FontAwesomeIcon icon={contandoAqui ? faPause : faPlay} />
            {contandoAqui
              ? formatarCronometro(segundosCorrentes)
              : segundosTarefa > 0
              ? formatarDuracao(segundosTarefa)
              : null}
          </button>

          {ehSubtarefa && (
            <button
              type="button"
              className={`tc-dispensar ${dispensada ? "ativo" : ""}`}
              onClick={() => alternarNaoSeAplica(task)}
              title={dispensada ? "Voltar a valer" : "Nao se aplica"}
            >
              <FontAwesomeIcon icon={faBan} />
            </button>
          )}

          {task.projeto && <span className="tc-projeto">{task.projeto}</span>}

          <span className={`tc-prioridade ${getTaskPriorityClass(task.prioridade)}`}>
            {getTaskPriorityLabel(task.prioridade)}
          </span>

          <span className="tc-dono">{getResponsavelNome(task) || "Sem responsavel"}</span>

          <span className={`tc-prazo ${atrasada ? "atrasada" : ""}`}>
            {prazo || "Sem prazo"}
          </span>
        </div>
      </article>
    );
  }

  if (!carregandoWorkspaces && !selectedWorkspaceId) {
    return (
      <div className="tc-page">
        <Alert variant="info">
          Selecione um workspace no topo para ver as tarefas.
        </Alert>
      </div>
    );
  }

  return (
    <div className="tc-page">
      <header className="tc-header">
        <div>
          <h4>Tarefas</h4>
          <small>
            {metricas.total} tarefa(s)
            {metricas.atrasadas > 0 && ` · ${metricas.atrasadas} atrasada(s)`}
            {metricas.hoje > 0 && ` · ${metricas.hoje} para hoje`}
            {metricas.segundos > 0 && ` · ${formatarDuracao(metricas.segundos)} apontadas`}
          </small>
        </div>

        <div className="tc-header-acoes">
          <Button
            variant="outline-secondary"
            className="tc-nova"
            onClick={() => setMostrarAplicarModelo(true)}
            title="Criar uma tarefa com checklist a partir de um modelo"
          >
            <FontAwesomeIcon icon={faDiagramProject} /> Usar modelo
          </Button>

          <Button className="tc-nova" onClick={novaTarefa}>
            <FontAwesomeIcon icon={faPlus} /> Nova tarefa
          </Button>
        </div>
      </header>

      {/* ─── Views ─────────────────────────────────────────────────────────── */}
      <nav className="tc-views">
        {views.map((view) => {
          const compartilhada = ehCompartilhada(view);
          const personalizada = compartilhada && adaptaAoLeitor(view);

          return (
            <button
              key={view.id}
              type="button"
              className={`tc-view ${String(viewAtivaId) === String(view.id) ? "ativa" : ""}`}
              onClick={() => aplicarView(view)}
              title={descreverView(view, contextoDescricao)}
            >
              {compartilhada && (
                <FontAwesomeIcon icon={faUserGroup} className="tc-view-icone" />
              )}

              {view.nome}

              {personalizada && (
                <FontAwesomeIcon
                  icon={faWandMagicSparkles}
                  className="tc-view-adapta"
                  title="Cada pessoa ve as tarefas dela nesta view"
                />
              )}

              {!view.sistema && view.minha && (
                <span
                  className="tc-view-acao"
                  role="button"
                  tabIndex={-1}
                  title="Excluir view"
                  aria-label={`Excluir view ${view.nome}`}
                  onClick={(evento) => excluirView(evento, view)}
                >
                  <FontAwesomeIcon icon={faTrash} />
                </span>
              )}

              {!view.sistema && !view.minha && (
                <span
                  className="tc-view-acao"
                  role="button"
                  tabIndex={-1}
                  title={`De ${view.autor || "outra pessoa"} — ocultar da minha barra`}
                  aria-label={`Ocultar view ${view.nome}`}
                  onClick={(evento) => ocultarView(evento, view)}
                >
                  <FontAwesomeIcon icon={faEyeSlash} />
                </span>
              )}
            </button>
          );
        })}

        {viewsOcultas.length > 0 && (
          <button
            type="button"
            className="tc-view tc-view-ocultas"
            onClick={() => {
              viewsOcultas.forEach((id) => alternarViewOculta(selectedWorkspaceId, id));
              setViewsOcultas(carregarViewsOcultas(selectedWorkspaceId));
            }}
            title="Mostrar de novo as views do time que voce ocultou"
          >
            +{viewsOcultas.length} oculta(s)
          </button>
        )}

        <button
          type="button"
          className={`tc-view tc-view-salvar ${alterada ? "destaque" : ""}`}
          onClick={() => {
            setErroView("");
            setMostrarSalvarView(true);
          }}
          title="Salvar filtros e agrupamento atuais como uma view"
        >
          <FontAwesomeIcon icon={faBookmark} /> {alterada ? "Salvar view" : "Salvar como"}
        </button>
      </nav>

      <p className="tc-descricao">{descreverView(viewAtiva, contextoDescricao)}</p>

      {/* ─── Toolbar ───────────────────────────────────────────────────────── */}
      <div className="tc-toolbar">
        <Form.Control
          className="tc-busca"
          type="search"
          placeholder="Buscar tarefa, projeto, responsavel ou tag..."
          value={filtros.busca}
          onChange={(evento) => atualizarFiltro("busca", evento.target.value)}
        />

        <Button
          variant={mostrarFiltros ? "dark" : "outline-secondary"}
          onClick={() => setMostrarFiltros((atual) => !atual)}
        >
          <FontAwesomeIcon icon={faFilter} /> Filtros
          {filtrosAtivos > 0 && <Badge bg="warning" className="ms-2">{filtrosAtivos}</Badge>}
        </Button>

        <div className="tc-agrupar">
          <FontAwesomeIcon icon={faLayerGroup} />
          <Form.Select
            value={agruparPor}
            onChange={(evento) => setAgruparPor(evento.target.value)}
            disabled={modo === VIEW_MODE.QUADRO}
            title={
              modo === VIEW_MODE.QUADRO
                ? "No quadro, as colunas sao sempre as etapas"
                : "Agrupar por"
            }
          >
            {GROUP_BY_OPTIONS.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Form.Select>
        </div>

        <div className="tc-modos">
          <button
            type="button"
            className={modo === VIEW_MODE.LISTA ? "ativo" : ""}
            onClick={() => setModo(VIEW_MODE.LISTA)}
            title="Lista"
          >
            <FontAwesomeIcon icon={faList} />
          </button>
          <button
            type="button"
            className={modo === VIEW_MODE.QUADRO ? "ativo" : ""}
            onClick={() => setModo(VIEW_MODE.QUADRO)}
            title="Quadro"
          >
            <FontAwesomeIcon icon={faTableColumns} />
          </button>
        </div>

        <Form.Check
          type="switch"
          id="tc-ocultar"
          label="Ocultar concluidas"
          checked={ocultarConcluidas}
          onChange={(evento) => setOcultarConcluidas(evento.target.checked)}
        />
      </div>

      {mostrarFiltros && (
        <div className="tc-filtros">
          <Form.Select
            value={filtros.projeto}
            onChange={(evento) => atualizarFiltro("projeto", evento.target.value)}
          >
            <option value="all">Todos os projetos</option>
            {projetoOptions.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            value={filtros.responsavel}
            onChange={(evento) => atualizarFiltro("responsavel", evento.target.value)}
          >
            <option value="all">Qualquer responsavel</option>
            <option value={RESPONSAVEL_EU}>Eu</option>
            <option value={RESPONSAVEL_SEM_DONO}>Sem responsavel</option>
            {responsavelOptions.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            value={filtros.prazo}
            onChange={(evento) => atualizarFiltro("prazo", evento.target.value)}
          >
            {OPCOES_PRAZO.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            value={filtros.prioridade}
            onChange={(evento) => atualizarFiltro("prioridade", evento.target.value)}
          >
            <option value="all">Qualquer prioridade</option>
            {TASK_PRIORITY_OPTIONS.map((opcao) => (
              <option key={opcao.value} value={String(opcao.value)}>{opcao.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            value={filtros.status}
            onChange={(evento) => atualizarFiltro("status", evento.target.value)}
          >
            {OPCOES_STATUS.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Form.Select>

          {tags.length > 0 && (
            <Form.Select
              value={filtros.tag}
              onChange={(evento) => atualizarFiltro("tag", evento.target.value)}
            >
              <option value="all">Qualquer tag</option>
              {tags.map((tag) => (
                <option key={tag.id} value={String(tag.id)}>{tag.nome}</option>
              ))}
            </Form.Select>
          )}

          <Button variant="outline-secondary" onClick={() => setFiltros(FILTROS_VAZIOS)}>
            Limpar
          </Button>
        </div>
      )}

      {erro && <Alert variant="danger" className="mt-3">{erro}</Alert>}

      {/* ─── Acoes em lote ─────────────────────────────────────────────────── */}
      {selecionadas.length > 0 && (
        <div className="tc-lote">
          <span>{selecionadas.length} selecionada(s)</span>

          <Button
            size="sm"
            variant="success"
            disabled={aplicandoLote}
            onClick={() => aplicarEmLote({ concluido: true })}
          >
            <FontAwesomeIcon icon={faCheck} /> Concluir
          </Button>

          <Form.Select
            size="sm"
            value=""
            disabled={aplicandoLote}
            onChange={(evento) =>
              evento.target.value &&
              aplicarEmLote({ id_usuario_responsavel: Number(evento.target.value) })
            }
          >
            <option value="">Atribuir a...</option>
            {responsavelOptions.map((opcao) => (
              <option key={opcao.value} value={opcao.value}>{opcao.label}</option>
            ))}
          </Form.Select>

          <Form.Select
            size="sm"
            value=""
            disabled={aplicandoLote}
            onChange={(evento) =>
              evento.target.value && aplicarEmLote({ prioridade: Number(evento.target.value) })
            }
          >
            <option value="">Prioridade...</option>
            {TASK_PRIORITY_OPTIONS.map((opcao) => (
              <option key={opcao.value} value={String(opcao.value)}>{opcao.label}</option>
            ))}
          </Form.Select>

          <Form.Control
            size="sm"
            type="date"
            disabled={aplicandoLote}
            onChange={(evento) =>
              evento.target.value && aplicarEmLote({ vencimento: evento.target.value })
            }
          />

          <Button size="sm" variant="outline-secondary" onClick={() => setSelecionadas([])}>
            Cancelar
          </Button>
        </div>
      )}

      {/* ─── Conteudo ──────────────────────────────────────────────────────── */}
      {carregando ? (
        <div className="tc-carregando"><Spinner size="sm" /></div>
      ) : modo === VIEW_MODE.QUADRO ? (
        <TaskKanbanView
          user={user}
          workspaceId={selectedWorkspaceId}
          /* Subtarefa e passo de uma checklist, nao card de quadro. */
          tarefas={tarefasDaLista.filter((task) => !task.id_tarefa)}
          onTaskMoved={handleTaskMoved}
          onTaskClick={abrirTarefa}
        />
      ) : tarefasFiltradas.length === 0 ? (
        <div className="tc-vazio">
          <FontAwesomeIcon icon={faCheck} />
          <strong>Nada nesta view.</strong>
          <span>Troque de view acima ou ajuste os filtros.</span>
        </div>
      ) : (
        <div className="tc-grupos">
          {grupos.map((grupo) => {
            const idsDoGrupo = grupo.tarefas.map((task) => task.id);
            const todosMarcados =
              idsDoGrupo.length > 0 && idsDoGrupo.every((id) => selecionadas.includes(id));

            return (
              <section key={grupo.chave} className="tc-grupo">
                {grupo.titulo && (
                  <header className="tc-grupo-head">
                    <Form.Check
                      type="checkbox"
                      checked={todosMarcados}
                      onChange={() => alternarGrupo(grupo)}
                      aria-label={`Selecionar todas de ${grupo.titulo}`}
                    />
                    {grupo.cor && (
                      <span className="tc-grupo-cor" style={{ background: grupo.cor }} />
                    )}
                    <h5>{grupo.titulo}</h5>
                    <span className="tc-grupo-contador">{grupo.tarefas.length}</span>
                  </header>
                )}

                <div className="tc-linhas">
                  {grupo.tarefas.map((task) => {
                    const filhas = subtarefasPorPai.get(Number(task.id)) || [];
                    const aberto = expandidos.includes(task.id);

                    return (
                      <React.Fragment key={task.id}>
                        {renderLinha(task, { filhas, aberto })}

                        {aberto && filhas.length > 0 && (
                          <div className="tc-subtarefas">
                            {filhas.map((sub) => renderLinha(sub, { aninhada: true }))}
                          </div>
                        )}
                      </React.Fragment>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      <TaskModal
        show={mostrarModal}
        task={tarefaAberta}
        allowInboxTask
        onClose={() => setMostrarModal(false)}
        onSaved={carregar}
      />

      <AplicarModeloModal
        show={mostrarAplicarModelo}
        workspaceId={selectedWorkspaceId}
        projetos={projetoOptions}
        onHide={() => setMostrarAplicarModelo(false)}
        onAplicado={async (resultado) => {
          await carregar();
          // Abre a checklist recem-criada: e o que voce quer ver em seguida.
          if (resultado?.record?.id) setExpandidos((atual) => [...atual, resultado.record.id]);
        }}
      />

      <SalvarViewModal
        show={mostrarSalvarView}
        onHide={() => setMostrarSalvarView(false)}
        onSalvar={salvarView}
        viewAtual={viewAtiva}
        estado={{ filtros, agruparPor, modo, ocultarConcluidas }}
        contexto={contextoDescricao}
        salvando={salvandoView}
        erro={erroView}
      />
    </div>
  );
};

export default TarefasCentralPage;
